import { Directive, OnDestroy } from '@angular/core';
import { Exercicio, FuncaoService, GlobalService, Login, Relatorio } from 'eddydata-lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AnexoLRFService } from '../service/anexo-lrf.service';

@Directive()
export class GestaoFiscalLRF implements OnDestroy {
  protected funcaoService: FuncaoService;
  protected globalService: GlobalService;
  private login: Login = new Login();
  protected unsubscribe: Subject<void> = new Subject();

  constructor(
    protected anexoServico: AnexoLRFService,
    protected mes: number,
    protected exercicio: Exercicio) {
    this.globalService = new GlobalService();
    this.funcaoService = new FuncaoService();
    this.login = GlobalService.obterSessaoLogin();
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  // tslint:disable: max-line-length
  public montarRelatorio(orgaos: number[]) {
    this.anexoServico.obterRGF(this.mes, this.exercicio.id, orgaos, true)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(dados => {

        Relatorio.imprimirPersonalizado(
          `RELATÓRIO DE GESTÃO FISCAL - PODER EXECUTIVO`
          , this.login.usuario.nome,
          this.login.orgao.nome, this.login.brasao,
          this.conteudoReceita(dados),
          'portrait', 'RELATÓRIO DE GESTÃO FISCAL - PODER EXECUTIVO',
          {
            linhas: {
              hLineWidth() {
                return 1;
              },
              vLineWidth() {
                return 1;
              },
              hLineColor() {
                return 'black';
              },
              paddingLeft() {
                return 3;
              },
              paddingRight() {
                return 3;
              }
            }
          });
      });
  }

  private nomeQuadrimestre(mes: number) {
    if (mes === 1 || mes === 2 || mes === 3 || mes === 4) {
      return '1˚ QUADRIMESTRE';
    } else if (mes === 5 || mes === 6 || mes === 7 || mes === 8) {
      return '2˚ QUADRIMESTRE';
    } else if (mes === 9 || mes === 10 || mes === 11 || mes === 12) {
      return '3˚ QUADRIMESTRE';
    }
  }

  private conteudoReceita(dados: any[]): {}[] {

    // monta o cabecalho
    const registros: {}[] = [
      [{
        text: `REFERÊNCIA: ` +
          this.globalService.obterDataBR().monthNames[this.mes - 1].toLocaleUpperCase() + '/' + this.exercicio.ano,
        alignment: 'center',
        bold: true,
        colSpan: 3, border: [false, false, false, false],
      }, '', ''],
      [{
        text: 'LRF, art.48',
        alignment: 'left',
        bold: true,
        colSpan: 3, border: [false, false, false, false],
      }, '', ''],
      [{
        text: 'QUADRO COMPARATIVO COM OS LIMITES DA LRF: ',
        alignment: 'left',
        bold: true
      }, {
        text: this.nomeQuadrimestre(this.mes),
        alignment: 'center',
        bold: true,
        colSpan: 2
      }, '']
    ];

    // monta o agrupamento do relatório
    let valorRCL = 0;
    for (const item of dados) {
      if (item.relatorio === 'RCL') {
        valorRCL = item.valor;
        registros.push([
          { text: item.nome, bold: true },
          { text: this.funcaoService.convertToBrNumber(valorRCL, 2, true), bold: true, alignment: 'right', colSpan: 2 }, ''
        ]);
        registros.push([
          {
            text: '', border: [true, false, true, false], colSpan: 3, margin: [0, 15, 0, 0]
          }, '', ''
        ]);
        registros.push([
          { text: '' },
          { text: 'R$', alignment: 'center', bold: true },
          { text: '%', alignment: 'center', bold: true }
        ]);
      } else if (item.relatorio === 'DP') {
        registros.push([
          { text: item.nome, bold: true },
          { text: this.funcaoService.convertToBrNumber(item.valor, 2, true), alignment: 'right', bold: true },
          { text: this.funcaoService.convertToBrNumber((item.valor / valorRCL) * 100, 2, true), alignment: 'center', bold: true }
        ]);
        registros.push([
          { text: 'Limite Máximo (art. 20 LRF)' },
          { text: this.funcaoService.convertToBrNumber(+valorRCL * 0.54, 2, true), alignment: 'right' },
          { text: '54,00', alignment: 'center' },
        ]);
        const valorPrudencial = +valorRCL * 0.513;
        registros.push([
          { text: 'Limite Prudencial 95% (par.ún.art.22 LRF)' },
          { text: this.funcaoService.convertToBrNumber(valorPrudencial, 2, true), alignment: 'right' },
          { text: '51,30', alignment: 'center' },
        ]);

        registros.push([
          { text: 'Excesso a Regularizar' },
          { text: (+item.valor - (+valorRCL * 0.54)) > 0 ? this.funcaoService.convertToBrNumber(+item.valor - (+valorRCL * 0.54), 2, true) : '', alignment: 'right' },
          { text: (+item.valor - (+valorRCL * 0.54)) > 0 ? this.funcaoService.convertToBrNumber(((+item.valor - (+valorRCL * 0.54)) * 100) / +valorRCL, 2, true) : '', alignment: 'center' }
        ]);
        registros.push([
          {
            text: '', border: [true, false, true, false], colSpan: 3, margin: [0, 15, 0, 0]
          }, '', ''
        ]);
      } else if (item.relatorio === 'DCL') {
        registros.push([
          { text: item.nome, bold: true },
          { text: '' },
          { text: '' }
        ]);
        const valorDCL = +item.valor;
        registros.push([
          { text: 'Saldo Devedor' },
          { text: this.funcaoService.convertToBrNumber(valorDCL, 2, true), alignment: 'right' },
          { text: this.funcaoService.convertToBrNumber((valorDCL / valorRCL) * 100, 2, true), alignment: 'center' }
        ]);
        const valorLimite = +valorRCL * 1.2;
        registros.push([
          { text: 'Limite Legal (arts.3º e 4º Res.nº 40 Senado)' },
          { text: this.funcaoService.convertToBrNumber(valorLimite, 2, true), alignment: 'right' },
          { text: '120,00', alignment: 'center' }
        ]);
        registros.push([
          { text: 'Excesso a Regularizar' },
          { text: (+valorDCL - +valorLimite) > 0 ? this.funcaoService.convertToBrNumber(+valorDCL - +valorLimite, 2, true) : '', alignment: 'right' },
          { text: (+valorDCL - +valorLimite) > 0 ? this.funcaoService.convertToBrNumber(((+valorDCL - +valorLimite) * 100) / +valorLimite, 2, true) : '', alignment: 'center' }
        ]);
        registros.push([
          {
            text: '', border: [true, false, true, false], colSpan: 3, margin: [0, 15, 0, 0]
          }, '', ''
        ]);
      } else if (item.relatorio === 'CG') {
        registros.push([
          { text: item.nome, bold: true },
          { text: '' },
          { text: '' }
        ]);
        registros.push([
          { text: 'Montante' },
          { text: this.funcaoService.convertToBrNumber(item.valor, 2, true), alignment: 'right' },
          { text: this.funcaoService.convertToBrNumber((item.valor / valorRCL) * 100, 2, true), alignment: 'center' }
        ]);
        const valorLimite = +valorRCL * 0.22;
        registros.push([
          { text: 'Limite Legal (art. 9º Res.nº 43 Senado)' },
          { text: this.funcaoService.convertToBrNumber(valorLimite, 2, true), alignment: 'right' },
          { text: '22,00', alignment: 'center' }
        ]);
        registros.push([
          { text: 'Excesso a Regularizar' },
          { text: (+item.valor - +valorLimite) > 0 ? this.funcaoService.convertToBrNumber(+item.valor - +valorLimite, 2, true) : '', alignment: 'right' },
          { text: (+item.valor - +valorLimite) > 0 ? this.funcaoService.convertToBrNumber(((+item.valor - +valorLimite) * 100) / +valorLimite, 2, true) : '', alignment: 'center' }
        ]);
        registros.push([
          {
            text: '', border: [true, false, true, false], colSpan: 3, margin: [0, 15, 0, 0]
          }, '', ''
        ]);
      } else if (item.relatorio === 'OP') {
        registros.push([
          { text: item.nome, bold: true },
          { text: '' },
          { text: '' }
        ]);
        registros.push([
          { text: 'Realizadas no período' },
          { text: this.funcaoService.convertToBrNumber(item.valor, 2, true), alignment: 'right' },
          { text: this.funcaoService.convertToBrNumber((item.valor / valorRCL) * 100, 2, true), alignment: 'center' }
        ]);
        const valorLimite = +valorRCL * 0.16;
        registros.push([
          { text: 'Limite legal (inc. I, art. 7º Res.nº 43 Senado)' },
          { text: this.funcaoService.convertToBrNumber(valorLimite, 2, true), alignment: 'right' },
          { text: '16,00', alignment: 'center' }
        ]);
        registros.push([
          { text: 'Excesso a Regularizar' },
          { text: (+item.valor - +valorLimite) > 0 ? this.funcaoService.convertToBrNumber(+item.valor - +valorLimite, 2, true) : '', alignment: 'right' },
          { text: (+item.valor - +valorLimite) > 0 ? this.funcaoService.convertToBrNumber(((+item.valor - +valorLimite) * 100) / +valorLimite, 2, true) : '', alignment: 'center' }
        ]);
        registros.push([
          {
            text: '', border: [true, false, true, false], colSpan: 3, margin: [0, 15, 0, 0]
          }, '', ''
        ]);
      } else if (item.relatorio === 'ARO') {
        registros.push([
          { text: item.nome, bold: true },
          { text: '' },
          { text: '' }
        ]);
        registros.push([
          { text: 'Saldo devedor' },
          { text: this.funcaoService.convertToBrNumber(item.valor, 2, true), alignment: 'right' },
          { text: this.funcaoService.convertToBrNumber((item.valor / valorRCL) * 100, 2, true), alignment: 'center' }
        ]);
        const valorLimite = +valorRCL * 0.07;
        registros.push([
          { text: 'Limite legal (art. 10 Res.nº 43 Senado)' },
          { text: this.funcaoService.convertToBrNumber(valorLimite, 2, true), alignment: 'right' },
          { text: '7,00', alignment: 'center' }
        ]);
        registros.push([
          { text: 'Excesso a Regularizar' },
          { text: (+item.valor - +valorLimite) > 0 ? this.funcaoService.convertToBrNumber(+item.valor - +valorLimite, 2, true) : '', alignment: 'right' },
          { text: (+item.valor - +valorLimite) > 0 ? this.funcaoService.convertToBrNumber(((+item.valor - +valorLimite) * 100) / +valorLimite, 2, true) : '', alignment: 'center' }
        ]);
        registros.push([
          {
            text: '', border: [true, false, true, true], colSpan: 3, margin: [0, 15, 0, 0]
          }, '', ''
        ]);
      }
    }

    return [{
      layout: 'linhas',
      table: {
        dontBreakRows: true,
        headerRows: 2,
        widths: ['*', 120, 120],
        body: registros
      }
    }];
  }

}
